import { playVideoSafely } from './global-functions';

export default class VideoControls {
  constructor(context, parent) {
    this.element = context;
    this.parent = parent;
    this.init();
  }

  init() {
    const { element, parent } = this;
    let hasAutoplayVideo = false;

    parent.querySelectorAll('video').forEach((video) => {
      // Mark videos this button can play so CSS can hide the browser's own
      // blocked-playback overlay. Skipped when there is no custom button, or
      // when the video uses native controls, so the user is never left without
      // a way to start it.
      if (element && !video.hasAttribute('controls')) {
        video.classList.add('has-custom-video-controls');
      }

      if (video.hasAttribute('autoplay')) hasAutoplayVideo = true;
    });

    // One play pass if any video is set to autoplay. playVideo() already loops
    // every video in the parent, so calling it per autoplay video produced N²
    // play() calls on multi-video sections.
    if (hasAutoplayVideo) this.playVideo();

    element?.addEventListener('click', (e) => {
      e.stopImmediatePropagation();
      element.classList.contains('video-controls--paused')
        ? this.playVideo()
        : this.pauseVideo();
    });
  }

  /**
   * Plays every video in the parent, then sets the button to match what
   * actually happened. The state is applied after playback resolves so a
   * refused autoplay leaves a working Play affordance rather than a Pause icon
   * over a frozen video.
   */
  async playVideo() {
    const { parent } = this;
    const playAttempts = [];

    parent.querySelectorAll('video').forEach((video) => {
      playAttempts.push(playVideoSafely(video));
    });

    const results = await Promise.all(playAttempts);

    results.includes(true) ? this.markPlaying() : this.markPaused();
  }

  pauseVideo() {
    const { parent } = this;

    this.markPaused();
    parent.querySelectorAll('video').forEach((video) => {
      video.pause();
    });
  }

  /** Sets the control button to its playing state. */
  markPlaying() {
    const { element } = this;

    element?.classList.remove('video-controls--paused');
    element?.setAttribute('aria-label', 'Pause video');
  }

  /** Sets the control button to its paused state. */
  markPaused() {
    const { element } = this;

    element?.classList.add('video-controls--paused');
    element?.setAttribute('aria-label', 'Play video');
  }
}
